import * as Tabs from '@radix-ui/react-tabs';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	ArrowBigRight,
	ArrowUp,
	Bot,
	Braces,
	ChevronDown,
	FileText,
	Sparkles,
	Square,
	ThumbsDown,
	ThumbsUp,
	X,
} from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { UserRole } from 'models';
import { AddSystemDialog, ChatMarkdown, HeaderDropdown, PrintableReportHeader, Tooltip } from 'components';
import { isToolResponseUserPayload } from 'components/ChatMarkdown/facilitySwitchMessages';
import { setSelectedFacility, setSelectedSystem, standardApi, useAppDispatch, useAppSelector, useSystem } from 'store';
import { useAlert, useFilters } from 'context';
import { LoadingIndicator } from 'components/Select/subcomponents';
import { NotificationItemMinimal } from 'pages/Notifications';
import { useGetNotificationInboxQuery } from 'store/services/NotificationService';
import {
	cancelChatStream,
	ChatSubmission,
	useChatStreamQuery,
	useGetConversationQuery,
	useSubmitFeedbackMutation,
} from 'store/services/LLMService';
import { formatQueryTime, nurseSpinnerLabels, pickUnique } from 'pages/Dashboard/helpers/dashboardHelpers';
import { isCompletedAssistantMessage } from 'pages/Dashboard/facilitySwitch';
import { useNavigation } from 'context/NavigationProvider';
import { Routes } from 'models/navigation';
import { env, exportElementToPdf, userIsInternalAdmin } from 'utils';
import { attachmentPreview, useImageAttachments } from 'pages/Dashboard/useImageAttachments';
import { ImageAttachmentControls, ImageUploadButton } from 'pages/Dashboard/ImageAttachmentControls';
import { ImageAttachmentPreview } from 'pages/Dashboard/ImageAttachmentPreview';

import { ChatHistoryDialog } from './ChatHistoryDialog';
import { BenchmarkingTool } from './PromptProcessor';

type MatrixLoaderProps = {
	rows?: number;
	cols?: number;
	size?: number; // px
	gap?: number; // px
	activeCount?: number; // how many squares pulse at once
	interval?: number; // ms
	className?: string;
	activeColor?: string;
};

// Lightweight animated grid shown during loading states.
// Why: it provides visual activity without implying deterministic progress.
export const MatrixLoader: React.FC<MatrixLoaderProps> = ({
	rows = 4,
	cols = 6,
	size = 6,
	gap = 3,
	activeCount = 6,
	interval = 120,
	className = '',
	activeColor = 'bg-blue-400',
}) => {
	const total = rows * cols;

	const cells = useMemo<number[]>(() => {
		return Array.from({ length: total }, (_, i) => i);
	}, [total]);

	const [active, setActive] = useState<Set<number>>(() => pickUnique(activeCount, total));

	useEffect(() => {
		const id = window.setInterval(() => {
			setActive(pickUnique(activeCount, total));
		}, interval);

		return () => window.clearInterval(id);
	}, [activeCount, interval, total]);

	return (
		<div
			className={`grid ${className}`}
			style={{
				gridTemplateColumns: `repeat(${cols}, ${size}px)`,
				gap: `${gap}px`,
			}}
			aria-hidden='true'
		>
			{cells.map((i) => {
				const isOn = active.has(i);

				return (
					<div
						key={i}
						className={[
							'rounded-[2px] transition-all duration-200 ease-out',
							isOn ? `${activeColor} opacity-100 scale-110` : 'bg-gray-100 scale-90',
						].join(' ')}
						style={{ width: size, height: size }}
					/>
				);
			})}
		</div>
	);
};

// Dashboard-side notification panel shown on the landing/search screen.
// It refreshes when the dashboard loading state changes so users see newly fetched inbox items.
export function NotificationModule(props: { notifications: string[]; isLoading: boolean }) {
	const { data, refetch, isFetching } = useGetNotificationInboxQuery(undefined);

	useEffect(() => {
		refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.isLoading]);
	return (
		<div className='flex flex-col w-full h-80 bg-white'>
			<div className='bg-white font-semibold py-2 pt-0 text-[1.5em] rounded-md mb-1 text-blue-500 '>Welcome to Merlin</div>
			{props.isLoading && (
				<div className='text-gray-500 w-100 flex flex-col items-center justify-center h-60'>
					<div className='m-0 w-fit'>
						<LoadingIndicator />
					</div>
				</div>
			)}
			{data?.notifications
				.filter((item) => !item.read_status)
				.map((notification) => (
					<NotificationItemMinimal
						key={notification.id}
						id={notification.id}
						merlin_page_url={notification.merlin_page_url}
						category={''}
						subject={notification.subject}
						message={''}
						date={notification.date}
						recipient={notification.recipient}
					/>
				))}

			{!props.isLoading && data?.notifications.filter((item) => !item.read_status).length === 0 && !isFetching && (
				<div className='text-slate-400 text-center pt-16'>No new notifications</div>
			)}
		</div>
	);
}

// Main entry page for Merlin AI.
// This component switches between three experiences:
// 1) Search/landing screen, 2) Chat runtime, 3) Benchmarking tool.
export function Dashboard() {
	const images = useImageAttachments();
	const landingSubmitLock = useRef(false);
	const [inputText, setInputText] = useState('');
	const [isInputFocused, setIsInputFocused] = useState(false);
	const [activeTab, setActiveTab] = useState<'search' | 'chat' | 'benchmarking'>('search');
	// Carries the first prompt from landing/search into chat mode.
	const [firstSubmission, setFirstSubmission] = useState<ChatSubmission | null>(null);
	// Carries an existing conversation ID when user opens chat history.
	const [initialChatId, setInitialChatId] = useState<string | null>(null);
	const inputRef = useRef<any>(null);
	// Stores the latest chat id without triggering rerenders. The Dashboard unmount cleanup
	// uses this to prime the HOME nav link so users can resume the same chat later.
	const resumableChatIdRef = useRef<string | null>(null);
	const { setRoutePath, resetRoutePath, setNavItemDisplay, resetNavItemDisplay } = useNavigation();
	// Keep navigation mutators in refs so the mount-only effect below can use the latest
	// context functions during cleanup without rerunning whenever the provider value changes.
	const setRoutePathRef = useRef(setRoutePath);
	const resetRoutePathRef = useRef(resetRoutePath);
	const setNavItemDisplayRef = useRef(setNavItemDisplay);
	const resetNavItemDisplayRef = useRef(resetNavItemDisplay);

	useEffect(() => {
		setRoutePathRef.current = setRoutePath;
		resetRoutePathRef.current = resetRoutePath;
		setNavItemDisplayRef.current = setNavItemDisplay;
		resetNavItemDisplayRef.current = resetNavItemDisplay;
	}, [resetNavItemDisplay, resetRoutePath, setNavItemDisplay, setRoutePath]);

	useEffect(() => {
		console.log('Dashboard mounted');
		// Start every Dashboard mount from the default HOME nav state. This prevents a
		// stale "Resume Chat" override from a previous Dashboard session leaking into
		// a fresh landing/search experience.
		resetRoutePathRef.current('HOME');
		resetNavItemDisplayRef.current('HOME');

		// A `?c=` query param means the HOME nav link was primed with a resumable chat.
		// Load that conversation immediately, but while the user is actively in chat,
		// the sidebar action should be "New Chat" rather than "Resume Chat".
		const conversationId = new URLSearchParams(window.location.search).get('c');
		resumableChatIdRef.current = conversationId;
		if (conversationId) {
			setNavItemDisplayRef.current('HOME', { name: 'New Chat', icon: 'add_comment' });
			setInitialChatId(conversationId);
			setActiveTab('chat');
		}

		return () => {
			const resumableChatId = resumableChatIdRef.current;

			if (resumableChatId) {
				// Leaving Dashboard with a known chat id turns HOME into a resumable entry point.
				// The route points back to this chat, and the label/icon communicate that action.
				setRoutePathRef.current('HOME', `${Routes.HOME}?c=${encodeURIComponent(resumableChatId)}`);
				setNavItemDisplayRef.current('HOME', { name: 'Resume Chat', icon: 'forum' });
			} else {
				// No chat was created or loaded, so restore the normal HOME nav behavior.
				resetRoutePathRef.current('HOME');
				resetNavItemDisplayRef.current('HOME');
			}

			console.log('Dashboard unmounted');
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const starterExamples = [
		'Find the service line with the lowest FCOTS rate last quarter, then show the top surgeons in that service line and include their surgeon scorecards.',
		'Identify the room with the worst turnover performance, show its FCOTS trend over time, and pull the timeline for the worst day.',
		'Analyze block and procedure utilization trends for the last 6–12 months, flag the biggest risk areas, and provide the relevant scorecard details.',
	];
	const [placeholderIndex, setPlaceholderIndex] = useState(0);
	const [placeholderPhase, setPlaceholderPhase] = useState<'enter' | 'exit'>('enter');
	const rotatingPlaceholders = ['Ask anything…', ...starterExamples];

	const handleChatIdChange = useCallback((chatId: string | null) => {
		// ChatMessages owns chat creation/hydration, so keep the latest id here for
		// Dashboard cleanup. Also keep the active-chat nav label as "New Chat".
		resumableChatIdRef.current = chatId;
		setNavItemDisplayRef.current('HOME', { name: 'New Chat', icon: 'add_comment' });
	}, []);

	const handleExitChat = useCallback(() => {
		const resumableChatId = resumableChatIdRef.current;

		if (resumableChatId) {
			// Returning to the Dashboard landing view keeps the resumable conversation
			// available from the sidebar without navigating away from Dashboard.
			setNavItemDisplayRef.current('HOME', { name: 'Resume Chat', icon: 'forum' });
		} else {
			// If no chat id exists yet, the HOME item should fall back to its default label/icon.
			resetNavItemDisplayRef.current('HOME');
		}

		setActiveTab('search');
	}, []);

	// User context used by selectors and request scoping.
	const dispatch = useAppDispatch();
	const { selectedFacility, selectedSystem } = useAppSelector((state) => state.userState);
	const { isLoading, data, isFetching } = useSystem();
	const { filtersAreFetching } = useFilters();
	const isAdmin = userIsInternalAdmin(data?.user);
	const isHealthSystemAdmin = data?.user.role === UserRole.healthsystem_admin;
	const user_facilities = data?.user?.facilities.map((e) => e.id) ?? [];
	const system = data?.healthsystems.find((h) => h.id === selectedSystem);
	const facilities = data?.facilities;
	const facility = facilities?.find((f) => f.id === selectedFacility);
	const hasAiTokens = data?.user.has_ai_tokens ?? false;
	const isOutOfAiTokens = !hasAiTokens;

	// Landing input interaction state.
	const handleInputFocus = () => {
		if (isOutOfAiTokens) return;
		setIsInputFocused(true);
	};
	const handleInputBlur = () => {
		setIsInputFocused(false);
	};

	const handleSubmit = async () => {
		if (isOutOfAiTokens) return;
		if (filtersAreFetching) return;
		if ((!inputText.trim() && !images.files.length) || landingSubmitLock.current) return;
		landingSubmitLock.current = true;
		try {
			// Carry durable metadata across the landing/chat remount, rather than transferring blob URL ownership.
			setFirstSubmission(await images.submit(inputText));
		} catch {
			return;
		} finally {
			landingSubmitLock.current = false;
		}
		setInitialChatId(null);

		// Clear input
		setInputText('');
		if (inputRef.current) inputRef.current.textContent = '';

		// Hand off to chat mode; ChatMessages owns message creation + streaming lifecycle.
		// While inside chat, HOME acts as a "start over" affordance instead of a resume link.
		setNavItemDisplayRef.current('HOME', { name: 'New Chat', icon: 'add_comment' });
		setActiveTab('chat');
	};
	const handleKeyDown = (e: any) => {
		if (isOutOfAiTokens) return;
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSubmit();
		}
	};

	useEffect(() => {
		if (isOutOfAiTokens) return;
		if (inputText.trim()) return;

		const exitTimer = window.setTimeout(() => {
			setPlaceholderPhase('exit');
		}, 3200);

		const swapTimer = window.setTimeout(() => {
			setPlaceholderIndex((current) => (current + 1) % rotatingPlaceholders.length);
			setPlaceholderPhase('enter');
		}, 3650);

		return () => {
			window.clearTimeout(exitTimer);
			window.clearTimeout(swapTimer);
		};
	}, [inputText, isOutOfAiTokens, rotatingPlaceholders.length, placeholderIndex]);

	// Capture activeTab in a const to avoid type narrowing issues inside conditional blocks.
	const currentActiveTab = activeTab;

	if (activeTab === 'benchmarking') {
		return (
			<div className='h-[93vh] flex flex-col'>
				<Tabs.Root value={currentActiveTab} onValueChange={(value) => setActiveTab(value as any)}>
					<Tabs.List
						aria-label='Chat Tabs'
						className='flex border-b border-bluegray-100 dark:border-gray-700 bg-white dark:bg-gray-900'
					>
						<Tabs.Trigger
							value='chat'
							className={classNames(
								'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
								currentActiveTab === 'chat'
									? 'border-blue-500 text-blue-600 dark:text-blue-400'
									: 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
							)}
						>
							<Sparkles size={16} />
							Chat
						</Tabs.Trigger>
						<Tabs.Trigger
							value='benchmarking'
							className={classNames(
								'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
								currentActiveTab === 'benchmarking'
									? 'border-blue-500 text-blue-600 dark:text-blue-400'
									: 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
							)}
						>
							<Bot size={16} />
							Benchmarking Tool
						</Tabs.Trigger>
					</Tabs.List>
					<div className='flex-1 overflow max-h-[70em]'>
						<BenchmarkingTool />
					</div>
				</Tabs.Root>
			</div>
		);
	}

	// Chat mode mounts a separate component that owns streaming, queue polling, and feedback.
	if (activeTab === 'chat') {
		return (
			<ChatMessages
				initialSubmission={firstSubmission}
				initialChatId={initialChatId}
				onChatIdChange={handleChatIdChange}
				onExitChat={handleExitChat}
			/>
		);
	}
	// Default search/landing mode.
	return (
		<div className='min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-950 transition-all duration-700 mb-[20vh]'>
			{/* Ambient Background Elements */}
			<div className='absolute inset-0 overflow-hidden pointer-events-none'>
				<div className='absolute top-1/4 left-1/4 w-96 h-96 bg-blue-200/40 dark:bg-blue-800/10 rounded-full blur-3xl animate-pulse'></div>
				<div
					className='absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-200/30 dark:bg-purple-800/10 rounded-full blur-3xl animate-pulse'
					style={{ animationDelay: '1s' }}
				></div>
			</div>
			{/* Main Content */}
			{/* <ChatMarkdownDemo /> */}
			<div className='relative max-w-screen-md mx-auto px-4 md:px-8 flex flex-col h-screen text-p2'>
				<div className='flex-1 flex flex-col justify-center items-center'>
					{/* Logo - Desktop */}
					<div className='hidden md:block mb-6 transform transition-all duration-1000 ease-out'>
						<div className='relative group'>
							<div className='font-bold text-h3 bg-gradient-to-r from-green-600 via-blue-600 to-blue-600 bg-clip-text text-transparent text-center tracking-tight'>
								MerlinAI
							</div>
						</div>
					</div>
					{/* Search Input Container */}
					<div className='w-full relative group mb-[15vh]'>
						<div
							className={`absolute -inset-1 rounded-3xl opacity-0 ${
								isInputFocused ? 'opacity-20' : 'group-hover:opacity-10'
							} transition-all duration-500`}
						></div>
						<div className={`relative rounded-3xl border border-blue-500 transition-all duration-500 bg-white`}>
							<div className='flex flex-col'>
								{/* Main Input Area */}
								<div className='p-6'>
									<div className='relative'>
										<div
											ref={inputRef}
											className={classNames(
												'min-h-[3em] max-h-[45vh] overflow-auto outline-none resize-none bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-lg leading-relaxed',
												(filtersAreFetching || isOutOfAiTokens) && 'pointer-events-none opacity-60 cursor-not-allowed'
											)}
											contentEditable={!filtersAreFetching && !isOutOfAiTokens && !images.uploading}
											onPaste={images.paste}
											suppressContentEditableWarning
											onInput={(e) => setInputText(e.currentTarget.textContent || '')}
											onFocus={handleInputFocus}
											onBlur={handleInputBlur}
											onKeyDown={handleKeyDown}
											style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
										/>
										{!inputText && (
											<div className='absolute inset-0 pointer-events-none overflow-hidden'>
												<div
													className={classNames(
														'absolute inset-0 text-gray-400 dark:text-gray-500 text-lg',
														!isOutOfAiTokens && 'transition-all duration-450 ease-in-out will-change-transform',
														isInputFocused ? 'opacity-60' : 'opacity-100',
														!isOutOfAiTokens &&
															(placeholderPhase === 'enter' ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0')
													)}
												>
													<span className='inline-flex items-center gap-2'>
														{isOutOfAiTokens
															? 'You’ve reached your usage limit for now. Your balance refreshes on the first of the month. Contact your admin if you need more sooner.'
															: filtersAreFetching
															? 'Loading filters…'
															: rotatingPlaceholders[placeholderIndex]}
													</span>
												</div>
											</div>
										)}
									</div>
								</div>
								{/* Bottom Controls */}
								<ImageAttachmentControls images={images} disabled={isOutOfAiTokens || filtersAreFetching} />
								<div className='flex justify-between items-center px-3 py-2 border-t border-gray-100 text-[0.8em]'>
									{/* Health System and Facility Selectors */}
									<div className='flex items-center gap-1'>
										{isAdmin && (
											<HeaderDropdown
												activeText={system?.name ?? (isLoading ? '(Loading...)' : '(None)')}
												filterFn={(item: any, searchQuery: string) => {
													return item.name.toLowerCase().includes(searchQuery.toLowerCase());
												}}
												className='bg-blue-50 rounded-lg'
												label={<span className='hidden xl:inline text-gray-500'></span>}
												onSelect={(option: any) => {
													if (option.id !== selectedSystem) {
														dispatch(setSelectedSystem(option.id));
														dispatch(standardApi.util.resetApiState());
													}
												}}
												options={
													data?.healthsystems.filter((hs: any) => {
														return !(hs.name.at(0) === '!');
													}) ?? []
												}
												renderTextFn={(item: any) => item.name}
												hideUnderline
											/>
										)}
										{facilities && (
											<HeaderDropdown
												activeText={isLoading || isFetching ? '(Loading...)' : facility?.name ?? '(None)'}
												filterFn={(item: any, searchQuery: string) => {
													return item.name?.toLowerCase().includes(searchQuery.toLowerCase());
												}}
												className='bg-blue-50 rounded-lg'
												label={<span className='hidden xl:inline text-gray-500'></span>}
												onSelect={(option: any) => {
													dispatch(setSelectedFacility(option.id));
												}}
												isWorking={isLoading || isFetching}
												options={
													isLoading || isFetching
														? []
														: facilities.filter((e: any) => user_facilities?.includes(e.id) || isAdmin || isHealthSystemAdmin)
												}
												renderTextFn={(item: any) => item.name}
												hideUnderline
											/>
										)}
									</div>
									{/* Mode Selector */}
									{/* <div className='flex items-center'>
										<div className='flex items-center bg-blue-50/80 dark:bg-blue-900/30 backdrop-blur-sm rounded-2xl p-1 border border-blue-100/50 dark:border-blue-800/50'>
											<button
												onClick={() => setSelectedMode('search')}
												className={`flex items-center justify-center h-8 px-3 rounded-xl transition-all duration-300 ${
													selectedMode === 'search'
														? 'bg-white dark:bg-gray-800 shadow-lg text-blue-600 dark:text-blue-400 transform scale-105'
														: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-800/50'
												}`}
											>
												<Search size={14} />
											</button>
											<button
												onClick={() => setSelectedMode('research')}
												className={`flex items-center justify-center h-8 px-3 rounded-xl transition-all duration-300 ${
													selectedMode === 'research'
														? 'bg-white dark:bg-gray-800 shadow-lg text-blue-600 dark:text-blue-400 transform scale-105'
														: 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
												}`}
												disabled
											>
												<Zap size={14} />
											</button>
											<button
												onClick={() => setSelectedMode('studio')}
												className={`flex items-center justify-center h-8 px-3 rounded-xl transition-all duration-300 ${
													selectedMode === 'studio'
														? 'bg-white dark:bg-gray-800 shadow-lg text-blue-600 dark:text-blue-400 transform scale-105'
														: 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
												}`}
												disabled
											>
												<FlaskConical size={14} />
											</button>
										</div>
									</div> */}
									{/* Spacer */}
									<div></div>
									{/* Right Controls */}
									<div className='flex items-center justify-end gap-1'>
										{isAdmin && (
											<AddSystemDialog>
												<button
													type='button'
													className='group relative flex items-center justify-center h-8 w-10 rounded-xl text-blue-500 dark:text-blue-400 hover:bg-blue-100/80 dark:hover:bg-blue-700/80 transition-all duration-300 hover:scale-110 hover:text-blue-700 dark:hover:text-blue-200'
												>
													<Tooltip content={'Add System'} triggerType='span'>
														<span className='material-symbol-sm'>domain_add</span>
													</Tooltip>
												</button>
											</AddSystemDialog>
										)}
										<ImageUploadButton images={images} disabled={isOutOfAiTokens || filtersAreFetching} />
										{/* <button
											onClick={() => setActiveTab('benchmarking')}
											className='group relative flex items-center justify-center h-8 w-10 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100/80 dark:hover:bg-gray-700/80 transition-all duration-300'
											title='Benchmarking Tool'
										>
											<Bot size={14} />
										</button> */}
										<ChatHistoryDialog
											onSelectConversation={(conv) => {
												// Set the initial chat ID to load the existing conversation
												setInitialChatId(String(conv.conversation_id));
												setFirstSubmission(null);
												// Enter chat mode
												setActiveTab('chat');
											}}
										/>
										<button
											onClick={handleSubmit}
											disabled={
												images.uploading || isOutOfAiTokens || filtersAreFetching || (!inputText.trim() && !images.files.length)
											}
											className='relative flex items-center justify-center h-8 w-10 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-300 ml-3 group transform hover:scale-110 disabled:hover:scale-100'
										>
											<div className='absolute inset-0 rounded-xl bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300'></div>
											<div className='relative flex items-center justify-center'>
												<ArrowBigRight size={14} />
											</div>
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
					{/* Chat History */}
				</div>
			</div>
		</div>
	);
}

type Msg = {
	id: number;
	type: 'user' | 'assistant';
	content: string;
	files?: ReturnType<typeof attachmentPreview>[];
	timestamp: number;
	isLoading?: boolean;
	feedback?: 'thumbsUp' | 'thumbsDown' | null;
	message_id?: number; // DB message ID for feedback tracking
};

type SourceArgument = {
	name: string;
	value: string;
};

type ToolSourceEntry = {
	id: string;
	name: string;
	args: SourceArgument[];
	rawXml: string;
};

const SOURCE_META_TAGS = new Set([
	'task_progress',
	'attempt_completion',
	'ask_followup_question',
	'result',
	'switch_facility',
	'tool_response',
	'load_tool',
]);

const formatSourceToolName = (tagName: string) => {
	return tagName
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const stripXmlTags = (value: string) => {
	return value
		.replace(/<[^>]+>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
};

const parseSourceArguments = (rawXml: string, rootName: string): SourceArgument[] => {
	const args: SourceArgument[] = [];
	const rootOpenRe = new RegExp(`^\\s*<${rootName}\\b[^>]*>`);
	const rootCloseRe = new RegExp(`<\\/${rootName}>\\s*$`);
	const innerXml = rawXml.replace(rootOpenRe, '').replace(rootCloseRe, '');
	const childRe = /<([A-Za-z_][\w:-]*)\b[^>]*>([\s\S]*?)<\/\1>/g;
	let childMatch: RegExpExecArray | null;

	while ((childMatch = childRe.exec(innerXml))) {
		const [, childName, rawValue] = childMatch;
		if (!childName || childName === rootName || SOURCE_META_TAGS.has(childName)) continue;

		const value = stripXmlTags(rawValue ?? '');
		if (!value) continue;

		args.push({ name: childName, value });
	}

	return args;
};

// Extracts top-level XML tool calls from the raw assistant message. The rendered answer already hides
// these blocks in ChatMarkdown; this parser reuses the raw text as a transparent "Sources" trail.
const extractToolSourceEntries = (raw: string): ToolSourceEntry[] => {
	const entries: ToolSourceEntry[] = [];
	const openCounts = new Map<string, number>();
	const tagRe = /<\/?([A-Za-z_][\w:-]*)\b[^>]*>/g;
	const stack: Array<{ name: string; start: number; include: boolean }> = [];
	let match: RegExpExecArray | null;

	while ((match = tagRe.exec(raw))) {
		const fullTag = match[0];
		const name = match[1];
		const isClose = fullTag.startsWith('</');
		const isSelfClosing = /\/>\s*$/.test(fullTag);

		if (!name) continue;

		if (!isClose) {
			const isTopLevel = stack.length === 0;
			const include = isTopLevel && !SOURCE_META_TAGS.has(name);

			if (include) {
				openCounts.set(name, (openCounts.get(name) ?? 0) + 1);
			}

			if (!isSelfClosing) {
				stack.push({ name, start: match.index, include });
			} else if (include) {
				entries.push({
					id: `${name}-${openCounts.get(name) ?? 1}`,
					name,
					args: [],
					rawXml: fullTag,
				});
			}

			continue;
		}

		const openIndex = stack.map((item) => item.name).lastIndexOf(name);
		if (openIndex === -1) continue;

		const [opened] = stack.splice(openIndex, stack.length - openIndex);
		if (!opened?.include) continue;

		const rawXml = raw.slice(opened.start, tagRe.lastIndex).trim();
		entries.push({
			id: `${name}-${openCounts.get(name) ?? entries.length + 1}`,
			name,
			args: parseSourceArguments(rawXml, name),
			rawXml,
		});
	}

	return entries;
};

// Converts user/system/facility text into a safe, compact filename segment for PDF exports.
// Why: names can contain spaces or characters that are invalid in downloaded filenames, and keeping
// the segment short prevents long chat/message context from producing unwieldy file names.
const sanitizePdfFilenameSegment = (value: string) => {
	return value
		.trim()
		.replace(/[\\/:*?"<>|]+/g, '')
		.replace(/\s+/g, '_')
		.slice(0, 80);
};

// Allows print/export-only DOM changes to settle before html-to-PDF capture begins.
// Why: waiting two animation frames gives React layout updates, printable headers, and markdown/chart
// rendering a chance to paint so the exported PDF matches what the user sees.
const waitForPrintableRender = async () => {
	await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
	await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
};

const formatRateLimitMessage = (message: string) => {
	return message.replace(
		/(your limit will reset on\s+)(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z)/i,
		(fullMatch, prefix: string, timestamp: string) => {
			const retryAt = new Date(timestamp);
			if (Number.isNaN(retryAt.getTime())) return fullMatch;

			const localRetryAt = new Intl.DateTimeFormat(undefined, {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
				timeZoneName: 'short',
			}).format(retryAt);

			return `${prefix}${localRetryAt}`;
		}
	);
};

const toFacilityContextPayload = (facilityContext: any) => {
	if (!facilityContext) return undefined;

	return {
		alias: facilityContext.intraop_facility_name_alias,
		name: facilityContext.name,
		id: facilityContext.id,
	};
};

// Full chat experience used by Dashboard chat mode.
// Responsibilities: hydrate historical conversations, submit/stream new prompts,
// handle queued responses, render markdown/tool feed, and submit feedback.
type StreamRequest = {
	submission: ChatSubmission;
	chatId?: string;
	jobId?: string;
	bypassPacing?: boolean;
	bypassPacingUntilReset?: boolean;
	requestId: number;
};

const ChatMessages = ({
	initialSubmission,
	initialChatId,
	onChatIdChange,
	onExitChat,
}: {
	initialSubmission?: ChatSubmission | null;
	initialChatId?: string | null;
	onChatIdChange?: (chatId: string | null) => void;
	onExitChat?: () => void;
}) => {
	const { getAlertResponse } = useAlert();
	// Active user/system context is sent with each stream request for scoped responses.
	const { selectedSystem, selectedFacility } = useAppSelector((state) => state.userState);
	const { data: systemData } = useSystem();
	const canViewSources = systemData?.user?.role === UserRole.super_admin;
	const system = systemData?.healthsystems.find((h) => h.id === selectedSystem);
	const facilities = systemData?.facilities;
	const facility = facilities?.find((f) => f.id === selectedFacility);
	const currentFacility = useMemo(() => toFacilityContextPayload(facility), [facility]);
	const isAdmin = userIsInternalAdmin(systemData?.user);
	const isHealthSystemAdmin = systemData?.user.role === UserRole.healthsystem_admin;
	const availableFacilities = useMemo(() => {
		const userFacilities = systemData?.user?.facilities.map((e) => e.id) ?? [];
		return facilities
			?.filter((e: any) => !e.name?.startsWith('*') && (userFacilities.includes(e.id) || isAdmin || isHealthSystemAdmin))
			.map(toFacilityContextPayload);
	}, [facilities, isAdmin, isHealthSystemAdmin, systemData?.user?.facilities]);

	// Filters are included in query payload so responses align with current report context.
	const {
		endDate,
		earliest_date,
		startDate,
		surgeons,
		serviceLines,
		rooms,
		encounterTypes,
		blockNames,
		filtersAreFetching,
	} = useFilters();

	// Core chat timeline plus stream orchestration state.
	const [messages, setMessages] = useState<Msg[]>([]);
	const [pendingRequest, setPendingRequest] = useState<StreamRequest | null>(null);
	const [queuedJobId, setQueuedJobId] = useState<string | null>(null);
	const [queuePosition, setQueuePosition] = useState<number | null>(null);
	const [isQueued, setIsQueued] = useState(false);
	const [queuePollAfterMs, setQueuePollAfterMs] = useState(1000);

	// Refs are used for mutable coordination values that should not cause rerenders.
	const pollTimerRef = useRef<number | null>(null);
	const pendingPollSignatureRef = useRef<string | null>(null);
	const lastQueueTransitionKeyRef = useRef<string | null>(null);
	const handledRateLimitRequestIdRef = useRef<number | null>(null);
	const submitKickoffLockRef = useRef(false);
	const pendingRequestRef = useRef<StreamRequest | null>(null);
	const initialPromptKickoffRef = useRef(false);
	const requestSequenceRef = useRef(0);
	// Retain the complete request while queued or awaiting a pacing retry.
	const lastRequestRef = useRef<StreamRequest | null>(null);
	const [inputText, setInputText] = useState('');
	const images = useImageAttachments();
	const attachedFiles = images.files;
	const [rateLimitMessage, setRateLimitMessage] = useState<string | null>();
	const handleRateLimitOverride = async () => {
		const rejectedRequest = lastRequestRef.current;
		if (!rejectedRequest) return;
		const response = await getAlertResponse({
			title: 'Manage usage pacing',
			type: 'Warning',
			description:
				'Usage pacing helps spread your allocated usage across your monthly cycle. Bypassing it may use your remaining allocation sooner, leaving little or no usage available before your next reset. Choose how long to bypass pacing.',
			responseLayout: 'stacked',
			responseOptions: [
				{
					value: 'bypass_message',
					label: 'Bypass for this message',
					description: 'Continue with only the message that reached the pacing limit.',
				},
				{
					value: 'bypass_until_reset',
					label: 'Bypass until monthly reset',
					description: 'Turn off usage pacing for all messages until your allocation resets.',
				},
			],
			doNothingResponse: { value: 'close', label: 'Close' },
		});

		if (!['bypass_message', 'bypass_until_reset'].includes(response) || lastRequestRef.current !== rejectedRequest)
			return;
		if (
			!handleSubmitSubmission(rejectedRequest.submission, {
				bypassPacing: true,
				bypassPacingUntilReset: response === 'bypass_until_reset',
			})
		)
			return;
		setRateLimitMessage(null);
		images.discard(rejectedRequest.submission);
		// Remove only the restored prefix; preserve any edits or newer draft text.
		const draft = inputRef.current?.textContent || '';
		const prefix = rejectedRequest.submission.text;
		if (prefix && (draft === prefix || draft.startsWith(`${prefix}\n\n`))) {
			const remaining = draft.slice(prefix.length).replace(/^\n\n/, '');
			setInputText(remaining);
			if (inputRef.current) inputRef.current.textContent = remaining;
		}
	};
	// Initialize chatId with initialChatId if provided (for loading existing conversations)
	const [chatId, setChatId] = useState<string | null>(initialChatId ?? null);

	useEffect(() => {
		onChatIdChange?.(chatId);
	}, [chatId, onChatIdChange]);

	// Completion/settling flags control stop button state and query-time display timing.
	const [isAttemptComplete, setIsAttemptComplete] = useState(false);
	const [isResponseSettled, setIsResponseSettled] = useState(false);
	const [queryElapsedMs, setQueryElapsedMs] = useState(0);
	const showStyleGuide = env('SHOW_STYLEGUIDE', true);
	const [spinnerLabel, setSpinnerLabel] = useState<string>(() => {
		return nurseSpinnerLabels[Math.floor(Math.random() * nurseSpinnerLabels.length)] ?? 'Thinking';
	});
	const queryStartTimeRef = useRef<number | null>(null);
	const queryTimerIntervalRef = useRef<number | null>(null);
	const spinnerLabelIntervalRef = useRef<number | null>(null);

	// When entering from chat history, hydrate existing conversation before allowing follow-ups.
	const { data: conversationData, isLoading: isConversationLoading } = useGetConversationQuery(
		initialChatId ?? skipToken
	);

	// Normalize backend message shape into local UI message model.
	useEffect(() => {
		if (conversationData?.messages && conversationData.messages.length > 0) {
			const historicalMessages: Msg[] = conversationData.messages.map((msg, index) => ({
				id: index + 1,
				type: msg.sender_role === 'user' ? 'user' : 'assistant',
				content: msg.message_text,
				files: (msg.attachments ?? []).map(attachmentPreview),
				timestamp: new Date(msg.created_at).getTime(),
				isLoading: false,
				message_id: msg.message_id, // Store DB message ID for feedback tracking
				feedback: msg.feedback ?? null, // Include feedback from historical messages
			}));
			setMessages(historicalMessages);

			// Set initial feedback state based on the latest assistant message's feedback
			const assistantMessages = historicalMessages.filter((m) => m.type === 'assistant');
			const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];
			if (lastAssistantMessage?.feedback) {
				setFeedbackGiven(lastAssistantMessage.feedback);
			}
		}
	}, [conversationData]);

	const requestIdRef = useRef(0);

	// Build a stable stream request argument only when a request is active.
	// Using skipToken prevents the query hook from firing while idle.
	const streamArg = useMemo(() => {
		if (!pendingRequest) return skipToken;
		return {
			submission: pendingRequest.submission,
			chatId: pendingRequest.chatId,
			jobId: pendingRequest.jobId,
			bypassPacing: pendingRequest.bypassPacing,
			bypassPacingUntilReset: pendingRequest.bypassPacingUntilReset,
			requestId: pendingRequest.requestId, // stable per stream
			selectedSystem,
			selectedFacility,
			currentFacility,
			currentHealthSystem: system,
			availableFacilities,
			earliest_date: earliest_date?.toISOString(),
			end_date: endDate.toISOString(),
			start_date: startDate.toISOString(),
			surgeons: surgeons?.all,
			serviceLines: serviceLines?.all,
			rooms: rooms?.all,
			encounterTypes: encounterTypes?.all,
			blockNames: blockNames?.all,
		};
	}, [
		pendingRequest,
		selectedSystem,
		selectedFacility,
		currentFacility,
		system,
		availableFacilities,
		earliest_date,
		endDate,
		startDate,
		surgeons,
		serviceLines,
		rooms,
		encounterTypes,
		blockNames,
	]);

	const { data, isError: streamError, error: streamErrObj } = useChatStreamQuery(streamArg);

	const clearQueryTimerInterval = () => {
		if (queryTimerIntervalRef.current !== null) {
			window.clearInterval(queryTimerIntervalRef.current);
			queryTimerIntervalRef.current = null;
		}
	};

	const initializeQueryTimer = () => {
		clearQueryTimerInterval();
		queryStartTimeRef.current = Date.now();
		setQueryElapsedMs(0);
		setIsAttemptComplete(false);
		setIsResponseSettled(false);
	};

	const streamedText: string = data?.text ?? '';
	const isToolRunning: boolean = data?.isToolRunning ?? false;
	const retryAttempt: number = data?.retryAttempt ?? 0;
	const isRetrying = retryAttempt > 0;
	const isQueuePendingStatus = data?.queueStatus === 'queued' || data?.queueStatus === 'waiting';
	const transportStatus: 'idle' | 'queued' | 'reconnecting' = isRetrying
		? 'reconnecting'
		: isQueuePendingStatus || isQueued
		? 'queued'
		: 'idle';
	const streamDone = pendingRequest !== null && data?.done === true && data?.requestId === pendingRequest.requestId;
	// Drive UI loading from our own pending flag + the stream's "done"
	// Only show streaming indicator when there's an actual pending request
	const isStreaming = (pendingRequest !== null && !streamDone) || isQueued || isRetrying;
	const hasLiveStream = pendingRequest !== null || isQueued;
	const queuedResponseReceived =
		pendingRequest !== null &&
		data?.requestId === pendingRequest.requestId &&
		isQueuePendingStatus &&
		!!(data?.jobId || queuedJobId);

	const clearPollTimer = () => {
		if (pollTimerRef.current !== null) {
			window.clearTimeout(pollTimerRef.current);
			pollTimerRef.current = null;
		}
		pendingPollSignatureRef.current = null;
	};

	// Starts a new stream request with a monotonic request id.
	// Why: ids prevent stale updates from previous requests from mutating current UI state.
	const startStreamRequest = (args: Omit<StreamRequest, 'requestId'>, source: string) => {
		// Hard invariant: only one in-flight request at a time.
		if (pendingRequestRef.current) {
			console.debug('[chat-stream] request_blocked_already_in_flight', {
				source,
				activeRequestId: pendingRequestRef.current.requestId,
				activeJobId: pendingRequestRef.current.jobId,
			});
			return;
		}

		requestIdRef.current += 1;
		requestSequenceRef.current += 1;
		const nextPendingRequest = {
			...args,
			requestId: requestIdRef.current,
		};
		pendingRequestRef.current = nextPendingRequest;
		lastRequestRef.current = nextPendingRequest;
		console.debug('[chat-stream] request_start', {
			seq: requestSequenceRef.current,
			source,
			requestId: requestIdRef.current,
			jobId: args.jobId,
			hasChatId: !!args.chatId,
		});
		setPendingRequest(nextPendingRequest);
	};

	// Schedules queue polling for deferred backend jobs and deduplicates duplicate timers.
	const scheduleQueuePoll = (
		args: Omit<StreamRequest, 'requestId'> & {
			jobId: string;
			delayMs: number;
			source: string;
		}
	) => {
		const safeDelayMs = Math.max(1000, args.delayMs);
		const signature = `${args.jobId}|${args.chatId ?? ''}`;
		if (pendingPollSignatureRef.current === signature && pollTimerRef.current !== null) {
			return;
		}

		clearPollTimer();
		pendingPollSignatureRef.current = signature;
		console.debug('[chat-stream] poll_scheduled', {
			source: args.source,
			jobId: args.jobId,
			delayMs: safeDelayMs,
		});

		pollTimerRef.current = window.setTimeout(() => {
			pollTimerRef.current = null;
			pendingPollSignatureRef.current = null;
			startStreamRequest(
				{
					submission: args.submission,
					chatId: args.chatId,
					jobId: args.jobId,
					bypassPacing: args.bypassPacing,
					bypassPacingUntilReset: args.bypassPacingUntilReset,
				},
				`${args.source}:poll_fire`
			);
		}, safeDelayMs);
	};

	useEffect(() => {
		if (queryStartTimeRef.current === null) {
			clearQueryTimerInterval();
			return;
		}

		const shouldTick = !isResponseSettled && (pendingRequest !== null || isAttemptComplete);

		if (shouldTick) {
			clearQueryTimerInterval();
			queryTimerIntervalRef.current = window.setInterval(() => {
				if (queryStartTimeRef.current !== null) {
					setQueryElapsedMs(Date.now() - queryStartTimeRef.current);
				}
			}, 1000);
			return;
		}

		setQueryElapsedMs(Date.now() - queryStartTimeRef.current);
		clearQueryTimerInterval();
	}, [isAttemptComplete, isResponseSettled, pendingRequest]);

	useEffect(() => {
		pendingRequestRef.current = pendingRequest;
	}, [pendingRequest]);

	useEffect(() => {
		return () => {
			// Prevent leaked timers when unmounting chat view.
			clearPollTimer();
			clearQueryTimerInterval();
			if (spinnerLabelIntervalRef.current !== null) {
				window.clearInterval(spinnerLabelIntervalRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!isStreaming || isToolRunning) {
			if (spinnerLabelIntervalRef.current !== null) {
				window.clearInterval(spinnerLabelIntervalRef.current);
				spinnerLabelIntervalRef.current = null;
			}
			return;
		}

		const getNextSpinnerLabel = (current: string) => {
			if (nurseSpinnerLabels.length <= 1) return current;
			let next = current;
			while (next === current) {
				next = nurseSpinnerLabels[Math.floor(Math.random() * nurseSpinnerLabels.length)] ?? current;
			}
			return next;
		};

		const rotateLabel = () => {
			setSpinnerLabel((current) => getNextSpinnerLabel(current));
		};

		spinnerLabelIntervalRef.current = window.setInterval(rotateLabel, 2000);

		return () => {
			if (spinnerLabelIntervalRef.current !== null) {
				window.clearInterval(spinnerLabelIntervalRef.current);
				spinnerLabelIntervalRef.current = null;
			}
		};
	}, [isStreaming, isToolRunning]);

	// The first successful response can mint a new conversation id; persist it for follow-ups.
	useEffect(() => {
		if (data?.chatId && !chatId) {
			setChatId(data.chatId);
		}
	}, [data?.chatId, chatId]);

	// Seed chat with initial prompt for brand-new conversations.
	// We intentionally skip this when `initialChatId` exists, because history hydration owns initial messages.
	useEffect(() => {
		// Skip if we're loading a historical conversation (we already have messages from the API)
		if (!initialSubmission || initialChatId) return;
		if (initialPromptKickoffRef.current) return;
		initialPromptKickoffRef.current = true;

		const now = Date.now();
		setMessages([
			{
				id: 1,
				type: 'user',
				content: initialSubmission.text,
				files: initialSubmission.attachments.map(attachmentPreview),
				timestamp: now,
			},
			{ id: 2, type: 'assistant', content: '', timestamp: now, isLoading: true },
		]);
		// Start with no chatId for the initial submission.
		initializeQueryTimer();
		startStreamRequest({ submission: initialSubmission }, 'initial_prompt');
	}, [initialSubmission, initialChatId]);

	const inputRef = useRef<HTMLDivElement | null>(null);
	const chatEndRef = useRef<HTMLDivElement | null>(null);
	const messagesContainerRef = useRef<HTMLDivElement | null>(null);
	const downloadMenuRef = useRef<HTMLDivElement | null>(null);
	const printableTranscriptRef = useRef<HTMLDivElement | null>(null);
	const [printTarget, setPrintTarget] = useState<{ mode: 'response'; messageId: number } | { mode: 'all' } | null>(null);
	const [openDownloadMenuForMessageId, setOpenDownloadMenuForMessageId] = useState<number | null>(null);
	const [openSourcesForMessageId, setOpenSourcesForMessageId] = useState<number | null>(null);
	const [expandedSourceIds, setExpandedSourceIds] = useState<Set<string>>(() => new Set());
	const [exportingFooterMessageId, setExportingFooterMessageId] = useState<number | null>(null);

	const toggleSourceDetails = useCallback((sourceKey: string) => {
		setExpandedSourceIds((current) => {
			const next = new Set(current);
			if (next.has(sourceKey)) {
				next.delete(sourceKey);
			} else {
				next.add(sourceKey);
			}
			return next;
		});
	}, []);

	const facilitySystemLabel = [facility?.name, system?.name].filter(Boolean).join(' • ');
	const generatedAtLabel = new Date().toLocaleString();
	const printing = printTarget !== null;
	// Legacy frontend-generated tool acknowledgements remain internal. Assistant messages are never
	// hidden here: ChatMarkdown strips switch_facility XML while preserving todo/status content.
	const hiddenInternalMessageIds = useMemo(
		() =>
			new Set(
				messages
					.filter((message) => message.type === 'user' && isToolResponseUserPayload(message.content ?? ''))
					.map((message) => message.id)
			),
		[messages]
	);
	const visibleMessages = useMemo(
		() => messages.filter((message) => !hiddenInternalMessageIds.has(message.id)),
		[messages, hiddenInternalMessageIds]
	);
	const completedAssistantMessages = visibleMessages.filter(
		(message) => message.type === 'assistant' && !message.isLoading && !!message.content?.trim()
	);

	useEffect(() => {
		if (openDownloadMenuForMessageId === null) return;
		setOpenSourcesForMessageId(null);

		const scrollTimer = window.setTimeout(() => {
			const menu = downloadMenuRef.current;
			const messagesContainer = messagesContainerRef.current;
			if (!menu || !messagesContainer) return;

			const menuRect = menu.getBoundingClientRect();
			const containerRect = messagesContainer.getBoundingClientRect();
			const visibleBottom = Math.min(containerRect.bottom, window.innerHeight);
			const bottomBuffer = 16;
			const overflowAmount = menuRect.bottom + bottomBuffer - visibleBottom;

			if (overflowAmount > 0) {
				messagesContainer.scrollBy({ top: overflowAmount, behavior: 'smooth' });
			}
		}, 0);

		return () => window.clearTimeout(scrollTimer);
	}, [openDownloadMenuForMessageId]);

	useEffect(() => {
		if (openSourcesForMessageId !== null) {
			setOpenDownloadMenuForMessageId(null);
		}
		setExpandedSourceIds(new Set());
	}, [openSourcesForMessageId]);

	const printableMessages =
		printTarget?.mode === 'response'
			? completedAssistantMessages.filter((message) => message.id === printTarget.messageId)
			: printTarget?.mode === 'all'
			? completedAssistantMessages
			: [];

	const handleExportSingleResponsePdf = async (message: Msg) => {
		if (hasLiveStream || printing || message.type !== 'assistant' || !message.content?.trim()) return;
		setOpenDownloadMenuForMessageId(null);
		setExportingFooterMessageId(message.id);

		setPrintTarget({ mode: 'response', messageId: message.id });

		try {
			await waitForPrintableRender();

			const element = printableTranscriptRef.current;
			if (!element) return;

			const filenameContext = sanitizePdfFilenameSegment(facility?.name ?? system?.name ?? 'conversation');
			const messageContext = sanitizePdfFilenameSegment(`response_${message.id}`);
			await exportElementToPdf({
				element,
				filename: `merlin_ai_${filenameContext || 'conversation'}_${messageContext}.pdf`,
				margin: 10,
				blockSelector: '[data-pdf-block="true"]',
				blockGap: 6,
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error generating MerlinAI response PDF:', error);
		} finally {
			setPrintTarget(null);
			setExportingFooterMessageId(null);
		}
	};

	const handleExportAllResponsesPdf = async (launcherMessageId?: number) => {
		if (hasLiveStream || printing || completedAssistantMessages.length === 0) return;
		setOpenDownloadMenuForMessageId(null);
		setExportingFooterMessageId(launcherMessageId ?? null);

		setPrintTarget({ mode: 'all' });

		try {
			await waitForPrintableRender();

			const element = printableTranscriptRef.current;
			if (!element) return;

			const filenameContext = sanitizePdfFilenameSegment(facility?.name ?? system?.name ?? 'conversation');
			await exportElementToPdf({
				element,
				filename: `merlin_ai_${filenameContext || 'conversation'}_all_responses.pdf`,
				margin: 10,
				blockSelector: '[data-pdf-block="true"]',
				blockGap: 6,
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error generating MerlinAI all responses PDF:', error);
		} finally {
			setPrintTarget(null);
			setExportingFooterMessageId(null);
		}
	};

	// Keep the latest assistant "stub" in sync with accumulated streamed text.
	// Why: this avoids appending partial chunks as separate messages.
	useEffect(() => {
		if (!pendingRequest) return;
		setMessages((prev) => {
			if (prev.length === 0) return prev;
			const next = [...prev];
			// find last assistant message (the stub)
			let idx = next.length - 1;
			while (idx >= 0 && next[idx].type !== 'assistant') idx--;
			if (idx >= 0) {
				next[idx] = { ...next[idx], content: streamedText, isLoading: isStreaming };
			}
			return next;
		});
		// Removed: scrollToBottom() - don't auto-scroll during streaming
	}, [streamedText, isStreaming, pendingRequest]);

	const restoreSubmission = (submission: ChatSubmission) => {
		images.restore(submission);
		const draft = inputRef.current?.textContent || '';
		const restored = [submission.text, draft].filter(Boolean).join('\n\n');
		setInputText(restored);
		if (inputRef.current) inputRef.current.textContent = restored;
	};

	// Handle a pacing-limit response by undoing the optimistic chat update and
	// returning the rejected prompt to the editor so the user can bypass or retry.
	useEffect(() => {
		// RTK Query can still publish data from an older request. Only the 429 for
		// the request currently shown as pending is allowed to change this chat.
		if (!pendingRequest || data?.requestId !== pendingRequest.requestId || !data.rateLimitMessage) return;

		// Several dependency changes can rerun this effect for the same response.
		// Marking the request first keeps the rollback and draft restoration idempotent.
		if (handledRateLimitRequestIdRef.current === pendingRequest.requestId) return;
		handledRateLimitRequestIdRef.current = pendingRequest.requestId;

		// The original submission has reached a terminal response. Releasing this
		// lock allows the pacing dialog's bypass action to submit a replacement.
		submitKickoffLockRef.current = false;

		// The server returns a UTC retry timestamp; display it in the user's locale.
		setRateLimitMessage(formatRateLimitMessage(data.rateLimitMessage));

		// Submission optimistically added the rejected user message and an empty
		// assistant placeholder. Remove that pair only while the placeholder is
		// still empty; existing or partially streamed assistant content is preserved.
		setMessages((prev) => {
			const next = [...prev];
			const assistantMessage = next[next.length - 1];
			if (assistantMessage?.type !== 'assistant' || assistantMessage.content?.trim()) return prev;

			next.pop();
			const userMessage = next[next.length - 1];
			if (userMessage?.type === 'user' && userMessage.content === pendingRequest.submission.text) next.pop();
			return next;
		});
	}, [data?.rateLimitMessage, data?.requestId, pendingRequest]);

	// Request lifecycle: once stream marks done for current request, clear pending state.
	useEffect(() => {
		if (!pendingRequest || !streamDone) return;
		if (data?.errorMessage || data?.rateLimitMessage) restoreSubmission(pendingRequest.submission);
		if (!data?.rateLimitMessage) lastRequestRef.current = null;
		setIsAttemptComplete(true);
		clearPollTimer();
		setQueuedJobId(null);
		setQueuePosition(null);
		setIsQueued(false);
		pendingRequestRef.current = null;
		setPendingRequest(null);
	}, [streamDone, pendingRequest]);

	// Queue lifecycle:
	// - when backend responds with queued/waiting, stop active request and begin polling jobId
	// - when job transitions to running/streaming, clear queue indicators
	useEffect(() => {
		if (!pendingRequest) return;

		if (queuedResponseReceived) {
			const effectiveJobId = data?.jobId ?? queuedJobId;
			if (!effectiveJobId) return;
			const transitionKey = `${pendingRequest.requestId}|${effectiveJobId}|${data?.queueStatus ?? 'queued'}`;
			if (lastQueueTransitionKeyRef.current === transitionKey) {
				return;
			}
			lastQueueTransitionKeyRef.current = transitionKey;

			setQueuedJobId(effectiveJobId);
			setQueuePosition(typeof data?.queuePosition === 'number' ? data.queuePosition : null);
			setIsQueued(true);
			pendingRequestRef.current = null;
			setPendingRequest(null);

			const pollDelay = typeof data?.pollAfterMs === 'number' ? data.pollAfterMs : 1000;
			setQueuePollAfterMs(Math.max(1000, pollDelay));
			scheduleQueuePoll({
				submission: pendingRequest.submission,
				chatId: pendingRequest.chatId,
				jobId: effectiveJobId,
				bypassPacing: pendingRequest.bypassPacing,
				bypassPacingUntilReset: pendingRequest.bypassPacingUntilReset,
				delayMs: pollDelay,
				source: 'queue_transition',
			});
			return;
		}

		if (data?.queueStatus === 'running' || streamedText.length > 0) {
			clearPollTimer();
			setQueuedJobId(null);
			setIsQueued(false);
			setQueuePosition(null);
			setQueuePollAfterMs(1000);
		}
	}, [
		pendingRequest,
		queuedResponseReceived,
		data?.requestId,
		data?.jobId,
		data?.queuePosition,
		data?.pollAfterMs,
		data?.queueStatus,
		streamedText,
		queuedJobId,
	]);

	// Safety net: if we are queued but no poll timer is active, schedule the next poll.
	// This keeps queue heartbeat/polling alive even if a prior timer was lost.
	useEffect(() => {
		if (!isQueued || pendingRequest || !queuedJobId) return;
		if (pollTimerRef.current !== null) return;
		if (!lastRequestRef.current) return;

		scheduleQueuePoll({
			...lastRequestRef.current,
			chatId: chatId || undefined,
			jobId: queuedJobId,
			delayMs: queuePollAfterMs,
			source: 'queue_safety_net',
		});
	}, [isQueued, pendingRequest, queuedJobId, queuePollAfterMs, chatId]);

	// Error fallback: replace loading assistant message with a friendly error and reset orchestration state.
	useEffect(() => {
		if (!pendingRequest || !streamError || streamDone) return;
		restoreSubmission(pendingRequest.submission);
		lastRequestRef.current = null;
		pendingRequestRef.current = null;
		clearPollTimer();
		setQueuedJobId(null);
		setQueuePosition(null);
		setIsQueued(false);
		setPendingRequest(null);
		setIsResponseSettled(false);
		setMessages((prev) => {
			const next = [...prev];
			let idx = next.length - 1;
			while (idx >= 0 && next[idx].type !== 'assistant') idx--;
			if (idx >= 0) {
				next[idx] = { ...next[idx], content: `Sorry, there was a streaming error.`, isLoading: false };
			}
			return next;
		});
		console.error(streamErrObj);
	}, [streamError, streamErrObj, pendingRequest, streamDone]);

	// Shared submit path used by typed input and quick follow-up actions from markdown.
	// Explicit submissions keep quick actions independent of the composer draft.
	const handleSubmitSubmission = (
		submission: ChatSubmission,
		options: { bypassPacing?: boolean; bypassPacingUntilReset?: boolean } = {}
	) => {
		if (filtersAreFetching || (rateLimitMessage && !options.bypassPacing)) return false;
		if ((!submission.text.trim() && !submission.attachments.length) || hasLiveStream || pendingRequestRef.current)
			return false;
		const now = Date.now();

		// Add both messages in one pass to avoid id/order races
		setMessages((prev) => {
			const nextId = prev.length ? Math.max(...prev.map((m) => m.id)) + 1 : 1;
			return [
				...prev,
				{
					id: nextId,
					type: 'user',
					content: submission.text,
					files: submission.attachments.map(attachmentPreview),
					timestamp: now,
				},
				{
					id: nextId + 1,
					type: 'assistant',
					content: '',
					timestamp: now,
					isLoading: true,
				},
			];
		});

		clearPollTimer();
		setQueuedJobId(null);
		setQueuePosition(null);
		setIsQueued(false);
		initializeQueryTimer();
		startStreamRequest({ submission, chatId: chatId || undefined, ...options }, 'user_submit');
		return true;
	};

	const handleSubmit = async () => {
		if (filtersAreFetching || rateLimitMessage || hasLiveStream || submitKickoffLockRef.current) return;
		if (!inputText.trim() && !attachedFiles.length) return;
		submitKickoffLockRef.current = true;
		try {
			const submission = await images.submit(inputText);
			if (handleSubmitSubmission(submission)) {
				setInputText('');
				if (inputRef.current) inputRef.current.textContent = '';
			} else {
				images.restore(submission);
			}
		} catch {
			// The hook keeps partially uploaded drafts and reports the upload error.
		} finally {
			submitKickoffLockRef.current = false;
		}
	};

	const handleStop = () => {
		if (!pendingRequest && !isQueued) return;

		if (pendingRequest) {
			cancelChatStream(pendingRequest.requestId);
		}
		if (lastRequestRef.current) restoreSubmission(lastRequestRef.current.submission);
		lastRequestRef.current = null;
		clearPollTimer();
		setQueuedJobId(null);
		setQueuePosition(null);
		setIsQueued(false);

		// Important: clearing pendingRequest disables the stream query hook immediately.
		pendingRequestRef.current = null;
		setPendingRequest(null);
		setIsResponseSettled(false);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (filtersAreFetching) {
			e.preventDefault();
			return;
		}
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			if (rateLimitMessage) return;
			handleSubmit();
		}
	};

	// useEffect(() => {
	// 	// Only scroll when stream is done (user has finished reading all content)
	// 	if (data?.done) {
	// 		scrollToBottom();
	// 	}
	// }, [messages, data?.done]);

	// // Add scroll event listener to detect when user scrolls up
	// useEffect(() => {
	// 	const container = messagesContainerRef.current;
	// 	if (container) {
	// 		container.addEventListener('scroll', handleScroll);
	// 		return () => container.removeEventListener('scroll', handleScroll);
	// 	}
	// }, []);

	// Loading guard while hydrating messages from an existing conversation.
	const isLoadingConversation = isConversationLoading && messages.length === 0;

	// Feedback applies only to the latest completed assistant message.
	const [feedbackGiven, setFeedbackGiven] = useState<'thumbsUp' | 'thumbsDown' | null>(null);

	// Get the latest assistant message (non-loading)
	const latestAssistantMessage = visibleMessages.filter((m) => m.type === 'assistant' && !m.isLoading).pop();

	// RTK mutation for persisting thumbs up/down to backend.
	const [submitFeedback] = useSubmitFeedbackMutation();

	// Feedback handlers - sends feedback to backend API
	const handleThumbsUp = async (targetMessage = latestAssistantMessage) => {
		if (!chatId || !targetMessage) return;

		setFeedbackGiven('thumbsUp');
		setMessages((prev) =>
			prev.map((message) => (message.id === targetMessage.id ? { ...message, feedback: 'thumbsUp' } : message))
		);

		try {
			await submitFeedback({
				conversation_id: chatId,
				message_id: targetMessage.message_id, // Use the DB message_id
				feedback: 'thumbsUp',
			}).unwrap();
			console.log('Thumbs up feedback submitted successfully');
		} catch (error) {
			console.error('Error submitting thumbs up:', error);
		}
	};

	const handleThumbsDown = async (targetMessage = latestAssistantMessage) => {
		if (!chatId || !targetMessage) return;

		setFeedbackGiven('thumbsDown');
		setMessages((prev) =>
			prev.map((message) => (message.id === targetMessage.id ? { ...message, feedback: 'thumbsDown' } : message))
		);

		try {
			await submitFeedback({
				conversation_id: chatId,
				message_id: targetMessage.message_id, // Use the DB message_id
				feedback: 'thumbsDown',
			}).unwrap();
			console.log('Thumbs down feedback submitted successfully');
		} catch (error) {
			console.error('Error submitting thumbs down:', error);
		}
	};

	return (
		<div className='h-[93vh] flex flex-col transition-colors duration-200 pr-10'>
			{printing && printableMessages.length > 0 && (
				<div className='fixed left-[-10000px] top-0 z-[-1] w-[900px] bg-white text-slate-900'>
					<div ref={printableTranscriptRef} className='bg-white px-8 py-6 text-slate-900'>
						<section data-pdf-block='true' className='bg-white pb-3 text-slate-900'>
							<PrintableReportHeader bottomMargin='mb-2' className='gap-4 py-0' logoClassName='h-10'>
								<p className='text-[1.25em] font-bold leading-tight text-bluegray-900'>MerlinAI</p>
								{facilitySystemLabel && (
									<p className='mt-0.5 text-[0.85em] font-medium text-bluegray-500'>{facilitySystemLabel}</p>
								)}
								<p className='mt-0.5 text-[0.75em] font-medium text-bluegray-400'>Generated {generatedAtLabel}</p>
							</PrintableReportHeader>
						</section>
						{printableMessages.map((message) => (
							<section key={message.id} data-pdf-block='true' className='bg-white py-3 text-slate-900'>
								<div className='text-sm leading-relaxed text-slate-900'>
									<ChatMarkdown
										text={message.content ?? ''}
										fontSize='sm'
										instant
										settled
										lineSpacing='relaxed'
										showTaskProgress={false}
										showToolFeed={false}
										showQuestion={false}
									/>
								</div>
							</section>
						))}
					</div>
				</div>
			)}
			{/* Messages Container */}
			<div ref={messagesContainerRef} className='flex-1 overflow-y-auto slate-customscrollbar pt-12'>
				<div className='max-w-4xl mx-auto px-4 pb-4'>
					<div className='space-y-8'>
						{/* Loading state when fetching conversation from history */}
						{isLoadingConversation && (
							<div className='flex items-start gap-4'>
								<div className='flex-1'>
									<div className='rounded-2xl px-5 py-4'>
										<div className='flex items-center gap-3'>
											<MatrixLoader rows={3} cols={3} activeCount={3} interval={200} size={4} />
											<span className='ml-2 mt-0.5 text-[0.7em] text-gray-500 dark:text-gray-400'>Loading conversation...</span>
										</div>
									</div>
								</div>
							</div>
						)}

						{messages.map((message) => {
							if (hiddenInternalMessageIds.has(message.id)) return null;

							const isLatestAssistantMessage = message.id === latestAssistantMessage?.id;
							const isExportingThisMessage = exportingFooterMessageId === message.id;
							const shouldShowAssistantFooter = isLatestAssistantMessage || isExportingThisMessage;
							const messageFeedback = message.feedback ?? (isLatestAssistantMessage ? feedbackGiven : null);
							const sourceEntries = message.type === 'assistant' ? extractToolSourceEntries(message.content ?? '') : [];

							return (
								<div key={message.id} className='group'>
									{message.type === 'user' ? (
										<div className='flex justify-end'>
											<div className='flex items-start gap-3 max-w-[85%]'>
												<div className='flex flex-col items-end flex-1 gap-2'>
													{message.files && message.files.length > 0 && (
														<div className='flex flex-wrap justify-end gap-2'>
															{message.files.map((file) => (
																<ImageAttachmentPreview key={file.id} src={file.preview} name={file.name} type={file.type} />
															))}
														</div>
													)}
													{!!message.content?.trim() && (
														<div className='bg-blue-600 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2'>
															<p className='text-[0.875rem] text-white dark:text-gray-100 leading-relaxed'>{message.content}</p>
														</div>
													)}
												</div>
											</div>
										</div>
									) : (
										// Assistant message
										<div className='flex items-start gap-4'>
											<div className='flex-1 min-w-0'>
												<div className='prose prose-sm max-w-none text-gray-800 space-y-3'>
													<ChatMarkdown
														text={message.content ?? ''}
														fontSize='sm'
														instant={!!initialChatId}
														settled
														settledDelayMs={3000}
														isLoading={
															message.id === messages[messages.length - 1]?.id &&
															(isQueued ||
																isRetrying ||
																(message.isLoading && !message.content?.trim()) ||
																(isStreaming && streamedText === ''))
														}
														spinnerLabel={
															isRetrying
																? `Reconnecting... (attempt ${retryAttempt})`
																: isQueued
																? `Queued${typeof queuePosition === 'number' && queuePosition > 0 ? ` (#${queuePosition})` : ''}`
																: spinnerLabel
														}
														transportStatus={transportStatus}
														retryAttempt={retryAttempt}
														doneSseReceived={isCompletedAssistantMessage(message.isLoading)}
														onAskQuestion={(selectedOption) => {
															if (filtersAreFetching) return;
															// Submit the question directly using the selected option
															if (submitKickoffLockRef.current) return;
															handleSubmitSubmission({ text: selectedOption.trim(), attachments: [] });
														}}
														onStatusChange={(status) => {
															setIsResponseSettled(status.isSettled);
														}}
														lineSpacing='relaxed'
														showTaskProgress={message.id === messages[messages.length - 1]?.id}
														//showQuestion={message.id === messages[messages.length - 1]?.id}
														showToolFeed={message.id === messages[messages.length - 1]?.id}
														// Removed: onAnimationComplete - don't auto-scroll during streaming
													></ChatMarkdown>
												</div>

												{!message.isLoading && !!message.content?.trim() && (
													<div
														className={classNames(
															'text-[0.9em] relative mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-3 dark:border-gray-700 transition-all duration-200',
															shouldShowAssistantFooter
																? 'opacity-100'
																: 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto'
														)}
													>
														<div className='flex min-w-0 flex-wrap items-center gap-2'>
															<>
																{showStyleGuide && (
																	<span className='text-gray-400 dark:text-gray-500'>Query time: {formatQueryTime(queryElapsedMs)}</span>
																)}
																<span className='text-gray-400 dark:text-gray-500'>Was this helpful?</span>
																<button
																	type='button'
																	onClick={() => handleThumbsUp(message)}
																	className={`p-1.5 rounded-lg transition-all duration-200 ${
																		messageFeedback === 'thumbsUp'
																			? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
																			: 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-500'
																	}`}
																	title='Thumbs up'
																>
																	<ThumbsUp size={16} />
																</button>
																<button
																	type='button'
																	onClick={() => handleThumbsDown(message)}
																	className={`p-1.5 rounded-lg transition-all duration-200 ${
																		messageFeedback === 'thumbsDown'
																			? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
																			: 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-500'
																	}`}
																	title='Thumbs down'
																>
																	<ThumbsDown size={16} />
																</button>
															</>
														</div>
														<div className='relative ml-auto flex items-center gap-1.5'>
															{canViewSources && sourceEntries.length > 0 && (
																<div className='relative'>
																	<button
																		type='button'
																		onClick={() => setOpenSourcesForMessageId((current) => (current === message.id ? null : message.id))}
																		className={classNames(
																			'inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-all duration-200',
																			openSourcesForMessageId === message.id
																				? 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
																				: 'text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300'
																		)}
																		title='View tool-call sources for this response'
																	>
																		<Braces size={14} />
																		Sources
																		<span
																			className={classNames(
																				'rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
																				openSourcesForMessageId === message.id
																					? 'bg-white/80 text-blue-700 dark:bg-blue-400/10 dark:text-blue-200'
																					: 'bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400'
																			)}
																		>
																			{sourceEntries.length}
																		</span>
																	</button>
																	{openSourcesForMessageId === message.id && (
																		<div className='absolute right-0 z-20 mt-2 max-h-[28rem] w-[min(30rem,calc(100vw-2rem))] overflow-auto rounded-2xl bg-white p-2 text-xs shadow-xl shadow-slate-200/60 ring-1 ring-gray-100 dark:bg-gray-900 dark:shadow-black/30 dark:ring-gray-700/80'>
																			<div className='mb-2 flex items-start justify-between gap-3 px-2 pb-2 pt-1.5'>
																				<div className='min-w-0'>
																					<div className='flex items-center gap-2 font-semibold text-gray-800 dark:text-gray-100'>
																						<span className='grid h-6 w-6 place-items-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300'>
																							<Braces size={13} />
																						</span>
																						Sources
																					</div>
																					<div className='mt-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400'>
																						Tool calls used for this answer
																					</div>
																				</div>
																				<button
																					type='button'
																					onClick={() => setOpenSourcesForMessageId(null)}
																					className='rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200'
																					aria-label='Close sources'
																				>
																					<X size={14} />
																				</button>
																			</div>
																			<div className='space-y-1.5'>
																				{sourceEntries.map((source, sourceIndex) => {
																					const sourceKey = `${message.id}-${source.id}-${sourceIndex}`;
																					const isSourceExpanded = expandedSourceIds.has(sourceKey);

																					return (
																						<div key={sourceKey} className='rounded-xl bg-gray-50/80 dark:bg-white/[0.04]'>
																							<button
																								type='button'
																								onClick={() => toggleSourceDetails(sourceKey)}
																								className='flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-gray-100/80 dark:hover:bg-white/[0.07]'
																								aria-expanded={isSourceExpanded}
																							>
																								<span className='flex min-w-0 items-center gap-2.5'>
																									<span className='grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300'>
																										<Braces size={13} />
																									</span>
																									<span className='min-w-0'>
																										<span className='block truncate font-semibold text-gray-800 dark:text-gray-100'>
																											{formatSourceToolName(source.name)}
																										</span>
																										<span className='mt-0.5 block text-[11px] text-gray-500 dark:text-gray-400'>
																											{source.args.length} argument{source.args.length === 1 ? '' : 's'} · collapsed by default
																										</span>
																									</span>
																								</span>
																								<span className='flex shrink-0 items-center gap-2 text-gray-400 dark:text-gray-500'>
																									<span className='rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold text-gray-500 shadow-sm dark:bg-gray-950/40 dark:text-gray-400'>
																										#{sourceIndex + 1}
																									</span>
																									<ChevronDown
																										size={14}
																										className={classNames('transition-transform duration-200', isSourceExpanded && 'rotate-180')}
																									/>
																								</span>
																							</button>

																							{isSourceExpanded && (
																								<div className='px-3 pb-3 pt-1'>
																									{source.args.length > 0 ? (
																										<div className='overflow-hidden rounded-lg bg-white/80 shadow-sm dark:bg-gray-950/30'>
																											{source.args.map((arg, argIndex) => (
																												<div
																													key={`${sourceKey}-${arg.name}`}
																													className={classNames(
																														'grid grid-cols-[7.5rem,1fr] items-start gap-3 px-3 py-2',
																														argIndex > 0 && 'border-t border-gray-100/70 dark:border-white/5'
																													)}
																												>
																													<div className='truncate text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500'>
																														{arg.name}
																													</div>
																													<div className='break-words text-[11px] font-medium leading-relaxed text-gray-800 dark:text-gray-200'>
																														{arg.value}
																													</div>
																												</div>
																											))}
																										</div>
																									) : (
																										<div className='rounded-lg bg-white/80 px-3 py-2 text-gray-500 shadow-sm dark:bg-gray-950/30 dark:text-gray-400'>
																											No arguments found.
																										</div>
																									)}
																								</div>
																							)}
																						</div>
																					);
																				})}
																			</div>
																		</div>
																	)}
																</div>
															)}
															<button
																type='button'
																onClick={() =>
																	setOpenDownloadMenuForMessageId((current) => (current === message.id ? null : message.id))
																}
																disabled={hasLiveStream || printing}
																className='inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300 dark:disabled:text-gray-600 dark:disabled:hover:bg-transparent'
																title={hasLiveStream ? 'PDF export is available after the response finishes.' : 'Download PDF options'}
															>
																<FileText size={14} />
																{printing ? 'Exporting…' : 'PDF'}
																<ChevronDown
																	size={14}
																	className={classNames(
																		'transition-transform duration-200',
																		openDownloadMenuForMessageId === message.id && 'rotate-180'
																	)}
																/>
															</button>
															{openDownloadMenuForMessageId === message.id && (
																<div
																	ref={downloadMenuRef}
																	className='absolute right-0 z-20 mt-2 w-48 overflow-hidden rounded-xl border border-gray-100 bg-white p-1 text-xs shadow-xl shadow-slate-200/60 dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/30'
																>
																	<button
																		type='button'
																		onClick={() => handleExportSingleResponsePdf(message)}
																		className='flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
																	>
																		<FileText size={14} />
																		This response only
																	</button>
																	<button
																		type='button'
																		onClick={() => handleExportAllResponsesPdf(message.id)}
																		disabled={completedAssistantMessages.length === 0}
																		className='flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left font-medium text-gray-600 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:text-gray-300 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100 dark:disabled:text-gray-600'
																	>
																		<FileText size={14} />
																		Full conversation
																	</button>
																</div>
															)}
														</div>
													</div>
												)}
											</div>
										</div>
									)}
								</div>
							);
						})}
						<div ref={chatEndRef} />
					</div>
				</div>
			</div>

			{/* Input Area - Fixed at bottom */}
			<div className='flex-shrink-0 dark:bg-gray-900/80 backdrop-blur-sm'>
				<div className='max-w-4xl mx-auto px-4 py-0'>
					{/* Health System and Facility Selectors */}
					<div className='flex items-center gap-2 mb-3 text-[0.8em]'>
						{/* {isAdmin && (
							<HeaderDropdown
								activeText={system?.name ?? (isLoading ? '(Loading...)' : '(None)')}
								filterFn={(item: any, searchQuery: string) => {
									return item.name.toLowerCase().includes(searchQuery.toLowerCase());
								}}
								label={<span className='hidden xl:inline text-gray-500'></span>}
								onSelect={(option: any) => {
									if (option.id !== selectedSystem) {
										dispatch(setSelectedSystem(option.id));
										dispatch(standardApi.util.resetApiState());
									}
								}}
								options={
									systemData?.healthsystems.filter((hs: any) => {
										return !(hs.name.at(0) === '!');
									}) ?? []
								}
								renderTextFn={(item: any) => item.name}
								hideUnderline
							/>
						)}
						{facilities && (
							<HeaderDropdown
								activeText={isLoading || isFetching ? '(Loading...)' : facility?.name ?? '(None)'}
								filterFn={(item: any, searchQuery: string) => {
									return item.name?.toLowerCase().includes(searchQuery.toLowerCase());
								}}
								label={<span className='hidden xl:inline text-gray-500'></span>}
								onSelect={(option: any) => {
									dispatch(setSelectedFacility(option.id));
								}}
								isWorking={isLoading || isFetching}
								options={
									isLoading || isFetching
										? []
										: facilities.filter((e: any) => user_facilities?.includes(e.id) || isAdmin || isHealthSystemAdmin)
								}
								renderTextFn={(item: any) => item.name}
								hideUnderline
							/>
						)} */}
					</div>
					<ImageAttachmentControls images={images} disabled={hasLiveStream || filtersAreFetching} />
					<div className='flex gap-3 items-end'>
						<div className='flex-1 relative'>
							<div
								className={classNames(
									rateLimitMessage &&
										'overflow-hidden rounded-2xl border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 shadow-sm'
								)}
							>
								{rateLimitMessage && (
									<div
										role='alert'
										aria-live='assertive'
										className='animate-displayAlert flex flex-col gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between'
									>
										<span className='leading-5 text-[0.9em]'>{rateLimitMessage}</span>
										<button
											type='button'
											onClick={handleRateLimitOverride}
											className='text-[0.9em] shrink-0 self-start rounded-lg border border-amber-300 bg-white px-3 py-1.5 font-semibold text-amber-950 shadow-sm transition-colors hover:bg-amber-100 dark:border-amber-700 dark:bg-gray-800 dark:text-amber-100 dark:hover:bg-amber-900/50 sm:self-auto'
										>
											Override
										</button>
									</div>
								)}
								<div
									className={classNames(
										'relative bg-white dark:bg-gray-800 transition-all duration-200',
										rateLimitMessage
											? 'rounded-b-2xl'
											: 'rounded-full border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md focus-within:border-blue-300 dark:focus-within:border-blue-500 focus-within:shadow-md'
									)}
								>
									<div className='absolute left-1 top-1/2 -translate-y-1/2'>
										<ImageUploadButton images={images} disabled={hasLiveStream || filtersAreFetching} circular />
									</div>
									<div
										ref={inputRef}
										className={classNames(
											'min-h-[3rem] max-h-32 overflow-auto outline-none resize-none bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 pl-12 pr-12 py-3 text-sm leading-relaxed flex items-center',
											filtersAreFetching && 'pointer-events-none opacity-60 cursor-not-allowed'
										)}
										contentEditable={!filtersAreFetching && !images.uploading}
										onPaste={(e) => images.paste(e, !hasLiveStream && !images.uploading)}
										suppressContentEditableWarning
										onInput={(e) => setInputText(e.currentTarget.textContent || '')}
										onKeyDown={handleKeyDown}
										style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
									/>
									{!inputText && attachedFiles.length === 0 && (
										<div className='absolute left-12 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500 text-sm z-10 flex items-center h-full'>
											{filtersAreFetching ? 'Loading filters…' : 'Ask a follow-up question...'}
										</div>
									)}
									{hasLiveStream ? (
										<button
											onClick={handleStop}
											disabled={isAttemptComplete}
											className='absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center h-8 w-8 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200'
										>
											{isAttemptComplete ? <ArrowUp size={16} /> : <Square size={16} />}
										</button>
									) : (
										<button
											onClick={handleSubmit}
											disabled={
												!!rateLimitMessage ||
												images.uploading ||
												filtersAreFetching ||
												(!inputText.trim() &&
													attachedFiles.length === 0 &&
													messages.length > 0 &&
													messages[messages.length - 1].type === 'assistant' &&
													!messages[messages.length - 1].isLoading)
											}
											className='absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center h-8 w-8 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-all duration-200 disabled:opacity-50'
										>
											<ArrowUp size={16} />
										</button>
									)}
								</div>
							</div>
						</div>
					</div>
					{system?.name && facility?.name && (
						<div className='text-[0.8em] text-gray-500 dark:text-gray-400 h-0 my-1.5 text-center'>
							<span>{system.name}</span>
							{' • '}
							<span>{facility.name}</span> {' • '} MerlinAI can make mistakes
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default Dashboard;
